import React, { useState } from "react";
import { TOKENS } from "../tokens";
import { Card } from "./Card";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Sparkles, PartyPopper, ClipboardList, Megaphone, Clock, Info, X
} from "lucide-react";

export function EmployeeCalendarWidget({ announcements = [], holidays = [] }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date().toISOString().split("T")[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Helper to format Date to YYYY-MM-DD
  const formatDateStr = (y, m, d) => {
    const mm = String(m + 1).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  };

  const todayStr = new Date().toISOString().split("T")[0];

  // Navigation handlers
  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };
  const goToToday = () => {
    const now = new Date();
    setCurrentDate(now);
    setSelectedDay(now.toISOString().split("T")[0]);
  };

  // Build grid days for the month
  const firstDayIndex = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-based index (0=Mon, 6=Sun)
  const totalDaysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthTotalDays = new Date(year, month, 0).getDate();

  // Combine announcements and holidays array
  const allEvents = [...announcements];

  // Function to get active events/reminders on a specific date (dateStr: YYYY-MM-DD)
  const getEventsForDate = (dateStr) => {
    const evts = allEvents.filter(evt => {
      const start = evt.startDate || evt.date;
      const end = evt.endDate || evt.startDate || evt.date;
      return dateStr >= start && dateStr <= end;
    });

    // Check if Sunday
    const [y, m, d] = dateStr.split("-").map(Number);
    const isSunday = new Date(y, m - 1, d).getDay() === 0;
    if (isSunday && !evts.some(e => e.type === "holiday" || e.title.toLowerCase().includes("holiday"))) {
      evts.push({
        id: `sunday-${dateStr}`,
        title: "Weekly Holiday",
        description: "Default weekly holiday.",
        type: "holiday",
        startDate: dateStr,
        endDate: dateStr,
      });
    }
    return evts;
  };

  const selectedDayEvents = getEventsForDate(selectedDay);

  // Generate calendar grid cells
  const gridCells = [];

  // Padded previous month days
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    const dayNum = prevMonthTotalDays - i;
    const pDate = new Date(year, month - 1, dayNum);
    const dateStr = formatDateStr(pDate.getFullYear(), pDate.getMonth(), dayNum);
    gridCells.push({
      dateStr,
      dayNum,
      isCurrentMonth: false,
      events: getEventsForDate(dateStr)
    });
  }

  // Current month days
  for (let d = 1; d <= totalDaysInMonth; d++) {
    const dateStr = formatDateStr(year, month, d);
    gridCells.push({
      dateStr,
      dayNum: d,
      isCurrentMonth: true,
      events: getEventsForDate(dateStr)
    });
  }

  // Trailing next month days to complete 35 or 42 cells grid
  const remainingCells = (7 - (gridCells.length % 7)) % 7;
  for (let n = 1; n <= remainingCells; n++) {
    const nDate = new Date(year, month + 1, n);
    const dateStr = formatDateStr(nDate.getFullYear(), nDate.getMonth(), n);
    gridCells.push({
      dateStr,
      dayNum: n,
      isCurrentMonth: false,
      events: getEventsForDate(dateStr)
    });
  }

  return (
    <Card style={{
      marginTop: 14,
      padding: "14px",
      background: "#ffffff",
      borderRadius: 16,
      boxShadow: "0 4px 20px rgba(10, 31, 58, 0.06)",
      border: `1px solid ${TOKENS.border}`
    }}>
      {/* Calendar Header Controls */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 12
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10,
            background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: TOKENS.gold
          }}>
            <CalendarIcon size={16} />
          </div>
          <div>
            <h3 style={{
              fontFamily: "Fraunces, serif",
              fontSize: 15,
              fontWeight: 700,
              color: TOKENS.navyDeep,
              margin: 0
            }}>
              {monthNames[month]} {year}
            </h3>
            <span style={{ fontSize: 10, color: TOKENS.muted, fontWeight: 600 }}>
              Team Schedule & Reminders
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            onClick={goToToday}
            style={{
              padding: "4px 10px",
              borderRadius: 6,
              border: `1px solid ${TOKENS.gold}60`,
              background: "rgba(245,184,0,0.1)",
              color: TOKENS.navyDeep,
              fontSize: 10,
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            Today
          </button>

          <button
            onClick={prevMonth}
            style={{
              width: 28, height: 28, borderRadius: 6,
              border: `1px solid ${TOKENS.border}`,
              background: TOKENS.cream,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: TOKENS.navyDeep, cursor: "pointer"
            }}
          >
            <ChevronLeft size={14} />
          </button>

          <button
            onClick={nextMonth}
            style={{
              width: 28, height: 28, borderRadius: 6,
              border: `1px solid ${TOKENS.border}`,
              background: TOKENS.cream,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: TOKENS.navyDeep, cursor: "pointer"
            }}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Days of Week Header */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        textAlign: "center",
        marginBottom: 4
      }}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((dayName, idx) => (
          <div key={dayName} style={{
            fontSize: 10,
            fontWeight: 800,
            color: idx >= 5 ? TOKENS.goldDark : TOKENS.muted,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            padding: "2px 0"
          }}>
            {dayName}
          </div>
        ))}
      </div>

      {/* Month Days Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: 4
      }}>
        {gridCells.map(({ dateStr, dayNum, isCurrentMonth, events }) => {
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDay;
          const hasHoliday = events.some(e => e.type === "holiday");
          const hasCombinedTask = events.some(e => e.type === "combined_task");
          const hasAnnouncement = events.some(e => e.type === "announcement");
          const hasBirthday = events.some(e => e.type === "birthday");

          let bg = isSelected ? TOKENS.navyDeep : isToday ? "rgba(245,184,0,0.15)" : isCurrentMonth ? "#F8FAFC" : "#F1F5F9";
          let color = isSelected ? "#ffffff" : isToday ? TOKENS.navyDeep : isCurrentMonth ? TOKENS.ink : "#94A3B8";

          if (isSelected) {
            bg = TOKENS.navyDeep;
            color = "#ffffff";
          }

          return (
            <button
              key={dateStr}
              onClick={() => {
                setSelectedDay(dateStr);
                setIsModalOpen(true);
              }}
              style={{
                height: 36,
                borderRadius: 8,
                border: isSelected
                  ? `2px solid ${TOKENS.gold}`
                  : isToday
                  ? `2px solid ${TOKENS.gold}`
                  : "1px solid transparent",
                background: bg,
                color: color,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "2px 0",
                cursor: "pointer",
                position: "relative",
                transition: "all 0.15s ease-in-out"
              }}
            >
              <span style={{
                fontSize: 11,
                fontWeight: isToday || isSelected ? 800 : 600,
                lineHeight: 1
              }}>
                {dayNum}
              </span>

              {/* Event indicators */}
              <div style={{ display: "flex", gap: 2, alignItems: "center", marginTop: 2 }}>
                {hasHoliday && (
                  <span style={{
                    width: 4, height: 4, borderRadius: "50%",
                    background: isSelected ? TOKENS.gold : "#EF4444",
                    boxShadow: "0 0 3px rgba(239, 68, 68, 0.4)"
                  }} title="Holiday" />
                )}
                {hasCombinedTask && (
                  <span style={{
                    width: 4, height: 4, borderRadius: "50%",
                    background: isSelected ? "#93C5FD" : "#2563EB",
                    boxShadow: "0 0 3px rgba(37, 99, 235, 0.4)"
                  }} title="Combined Team Task" />
                )}
                {hasAnnouncement && (
                  <span style={{
                    width: 4, height: 4, borderRadius: "50%",
                    background: isSelected ? "#C084FC" : "#9333EA",
                    boxShadow: "0 0 3px rgba(147, 51, 234, 0.4)"
                  }} title="Announcement" />
                )}
                {hasBirthday && (
                  <span style={{
                    width: 4, height: 4, borderRadius: "50%",
                    background: isSelected ? "#F472B6" : "#EC4899",
                    boxShadow: "0 0 3px rgba(236, 72, 153, 0.4)"
                  }} title="Birthday" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected Date Summary Preview / Trigger Card */}
      <div style={{
        marginTop: 10,
        padding: "8px 12px",
        background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
        borderRadius: 10,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            padding: "4px 8px", borderRadius: 6,
            background: "rgba(201,162,39,0.2)",
            color: TOKENS.gold, fontSize: 11, fontWeight: 800
          }}>
            {selectedDay === todayStr ? "TODAY" : selectedDay}
          </div>
          <div style={{ fontSize: 11, fontWeight: 600 }}>
            {selectedDayEvents.length > 0 ? (
              <span>{selectedDayEvents.length} Active Notice(s)</span>
            ) : (
              <span style={{ color: "#9FB0C9" }}>No notices scheduled</span>
            )}
          </div>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          style={{
            background: TOKENS.gold, color: TOKENS.navyDeep,
            border: "none", borderRadius: 6,
            padding: "4px 10px", fontSize: 11, fontWeight: 800,
            cursor: "pointer"
          }}
        >
          Details
        </button>
      </div>

      {/* Date Details Modal / Drawer */}
      {isModalOpen && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(10, 25, 50, 0.65)",
          backdropFilter: "blur(4px)",
          zIndex: 999,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16
        }}>
          <div style={{
            background: "#ffffff",
            borderRadius: 20,
            maxWidth: 480, width: "100%",
            maxHeight: "85vh",
            display: "flex", flexDirection: "column",
            overflow: "hidden",
            boxShadow: "0 20px 50px rgba(0,0,0,0.25)"
          }}>
            {/* Modal Header */}
            <div style={{
              background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
              padding: "16px 20px",
              color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "space-between"
            }}>
              <div>
                <div style={{ fontSize: 11, color: TOKENS.goldLight, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Date Schedule Details
                </div>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, marginTop: 2 }}>
                  {new Date(selectedDay + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{
                  background: "rgba(255,255,255,0.15)", border: "none",
                  borderRadius: "50%", width: 32, height: 32,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", cursor: "pointer"
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: 20, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
              {selectedDayEvents.length === 0 ? (
                <div style={{ textAlign: "center", padding: "30px 10px", color: TOKENS.muted }}>
                  <Info size={36} color={TOKENS.gold} style={{ marginBottom: 10, opacity: 0.8 }} />
                  <div style={{ fontSize: 15, fontWeight: 700, color: TOKENS.navyDeep }}>Regular Working Day</div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>There are no special team announcements or holidays scheduled for this date.</div>
                </div>
              ) : (
                selectedDayEvents.map(evt => {
                  const isHoliday = evt.type === "holiday";
                  const isTask = evt.type === "combined_task";
                  const isBirthday = evt.type === "birthday";

                  const cardBg = isHoliday
                    ? "linear-gradient(135deg, #FEF2F2, #FFF1F2)"
                    : isTask
                    ? "linear-gradient(135deg, #EFF6FF, #F0F9FF)"
                    : isBirthday
                    ? "linear-gradient(135deg, #FDF2F8, #FCE7F3)"
                    : "linear-gradient(135deg, #FAF5FF, #F3E8FF)";

                  const borderColor = isHoliday ? "#FCA5A5" : isTask ? "#93C5FD" : isBirthday ? "#FBCFE8" : "#D8B4FE";
                  const badgeBg = isHoliday ? "#EF4444" : isTask ? "#2563EB" : isBirthday ? "#EC4899" : "#9333EA";

                  return (
                    <div key={evt.id} style={{
                      padding: 16,
                      borderRadius: 14,
                      background: cardBg,
                      border: `1.5px solid ${borderColor}`,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.03)"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 800, color: "#fff",
                          background: badgeBg, padding: "3px 10px", borderRadius: 10,
                          textTransform: "uppercase", letterSpacing: 0.5,
                          display: "inline-flex", alignItems: "center", gap: 4
                        }}>
                          {isHoliday ? <PartyPopper size={12} /> : isTask ? <ClipboardList size={12} /> : isBirthday ? <Sparkles size={12} /> : <Megaphone size={12} />}
                          {isHoliday ? "Holiday" : isTask ? "Combined Team Task" : isBirthday ? "Birthday Announcement" : "Announcement"}
                        </span>

                        <span style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, display: "flex", alignItems: "center", gap: 4 }}>
                          <Clock size={12} />
                          {evt.startDate === evt.endDate ? evt.startDate : `${evt.startDate} to ${evt.endDate}`}
                        </span>
                      </div>

                      <div style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 700, color: TOKENS.navyDeep, marginTop: 4 }}>
                        {evt.title}
                      </div>

                      {evt.description && (
                        <div style={{ fontSize: 13, color: TOKENS.ink, marginTop: 6, lineHeight: 1.4 }}>
                          {evt.description}
                        </div>
                      )}

                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, paddingTop: 8, borderTop: `1px solid ${borderColor}50`, fontSize: 11, color: TOKENS.muted }}>
                        <span>Target: <strong>{evt.targetTeam || "ALL TEAMS"}</strong></span>
                        <span>Posted by: <strong>{evt.createdBy || "Manager"}</strong></span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
